import { useRef } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactCard from '../components/ContactCard';

const emailWins = [
  {
    stat: '18%',
    label: 'list-to-customer conversion',
    copy: 'Achieved for an eCommerce brand with automated welcome, abandoned cart, and post-purchase flows.',
  },
  {
    stat: '34%',
    label: 'CTR uplift',
    copy: 'For a SaaS company after we rebuilt their onboarding sequence and improved segmentation.',
  },
  {
    stat: '5x',
    label: 'newsletter revenue',
    copy: 'Generated in six months by combining storytelling, offers, and behavioural triggers.',
  },
];

const emailFlows = [
  'Welcome + onboarding sequences',
  'Abandoned cart + browse recovery',
  'Lead magnet nurture + demo follow-up',
  'Reactivation + win-back campaigns',
  'Product launch + promotional blasts',
  'Lifecycle segmentation + lead scoring',
];

const emailProcess = [
  {
    name: 'Audit & Align',
    text: 'We deep-dive your data, segment health, and current flows to spot revenue leaks and quick wins.',
  },
  {
    name: 'Build & Automate',
    text: 'Design new journeys, craft persuasive copy, and build flows in your ESP with proper integrations.',
  },
  {
    name: 'Optimise & Scale',
    text: 'Launch testing roadmap, monitor deliverability, and roll out new campaigns that drive lifetime value.',
  },
];

export default function EmailMarketingLanding() {
  const contactRef = useRef(null);
  const handleTalkClick = () => {
    contactRef.current?.focusForm?.();
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-white via-sky-50 to-indigo-100 font-sans text-slate-900">
      <Navbar onTalkClick={handleTalkClick} />

      <main className="flex-1 pt-32">
        <section id="services" className="mx-auto max-w-6xl px-6">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-slate-500">Lifecycle marketing that prints revenue</p>
              <h1 className="mt-4 text-4xl font-semibold md:text-5xl">Email & SMS programs that sell on autopilot</h1>
              <p className="mt-4 text-lg text-slate-600">
                From onboarding to reactivation, we build automated journeys and conversion-ready campaigns that nurture
                your audience and close more deals.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={handleTalkClick}
                  className="inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow hover:opacity-90"
                >
                  Get a lifecycle audit →
                </button>
                <a
                  href="tel:14374944736"
                  className="inline-flex items-center justify-center rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 transition hover:bg-white"
                >
                  Talk now: 1‑437‑494‑4736
                </a>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {emailWins.map((win) => (
                <div key={win.label} className="rounded-3xl border border-slate-200 bg-white/70 p-5 text-center shadow-sm backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{win.label}</p>
                  <p className="mt-3 text-3xl font-semibold text-slate-900">{win.stat}</p>
                  <p className="mt-2 text-xs text-slate-500">{win.copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-6xl px-6">
          <div className="rounded-3xl border border-blue-100 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-2xl font-semibold text-slate-900">Lifecycle flows we map and manage</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
              {emailFlows.map((flow) => (
                <div key={flow} className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
                  {flow}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="work" className="mx-auto mt-16 max-w-6xl px-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
              <p className="text-sm uppercase tracking-[0.2em] text-slate-500">Client spotlight</p>
              <h2 className="mt-2 text-3xl font-semibold text-slate-900">Automated welcome journey fuels 28% revenue lift</h2>
              <p className="mt-4 text-sm text-slate-600">
                We reimagined the first 30 days of communication for a DTC brand, layering in behaviour-based triggers and
                dynamic offers. Revenue from email jumped by 28% in a single quarter.
              </p>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
              <p className="text-sm uppercase tracking-[0.2em] text-slate-500">Tech we work with</p>
              <p className="mt-4 text-sm text-slate-600">
                Klaviyo, HubSpot, ActiveCampaign, Customer.io, Mailchimp, Sendlane, Drip, Shopify, WooCommerce, and custom stacks —
                we plug seamlessly into your existing tools.
              </p>
            </div>
          </div>
        </section>

        <section id="process" className="mx-auto mt-16 max-w-5xl px-6">
          <div className="rounded-3xl border border-slate-200 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-3xl font-semibold text-slate-900 text-center md:text-left">Our lifecycle growth framework</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {emailProcess.map((step) => (
                <div key={step.name} className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-500">{step.name}</h3>
                  <p className="mt-3 text-sm text-slate-600">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-4xl px-6 text-center">
          <div className="rounded-3xl border border-blue-100 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-3xl font-semibold text-slate-900 md:text-4xl">Ready to unlock more revenue from your list?</h2>
            <p className="mt-3 text-lg text-slate-600">
              Book your lifecycle roadmap session and we’ll outline the flows, campaigns, and tests that will deliver results fastest.
            </p>
            <button
              type="button"
              onClick={handleTalkClick}
              className="mt-6 inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-white shadow hover:opacity-90"
            >
              Plan my lifecycle program →
            </button>
          </div>
        </section>
      </main>

      <ContactCard ref={contactRef} className="mt-16" />
      <Footer />
    </div>
  );
}
